"use client";

import { useState } from "react";
import {
  Info,
  CircleCheck,
  TriangleAlert,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BUTTON_BASE, BUTTON_VARIANT, type ButtonVariant } from "./buttons";

export type { ButtonVariant } from "./buttons";

/**
 * The shared component kit, beside the tokens it renders with (R22 T10).
 *
 * **Both frontends import these.** They were `src/app/_components/ui.tsx` while
 * the Next app was the only reader; the static web app needs the same Field,
 * the same Banner and the same Card, and a second copy of them would drift the
 * moment either side fixed a padding. The design system is a promise about how
 * Bookr looks, and a promise kept in two files is a coincidence.
 *
 * **Framework-free by construction.** Nothing here imports `next/*`, and the
 * one primitive that could not manage it stayed behind: `SubmitButton` reads
 * `useFormStatus`, which only means anything inside a Next form action, so it
 * lives with the host that has them. The static app passes `pending` itself.
 *
 * Tokens only, never raw hex (`./design-system.css`). Motion is 120 to 180ms
 * ease-out and all of it collapses under prefers-reduced-motion, handled once
 * in the stylesheet rather than per component.
 */

/**
 * The badge vocabulary: two hues and a neutral, no more.
 *
 * Defined here rather than imported from Booking, even though Booking's
 * `StatusTone` is the same five words. This is the design layer and it imports
 * nothing; the two are structurally identical, so a booking's tone passes
 * straight into `StatusBadge` without either side depending on the other.
 */
export type Tone = "success" | "danger" | "warning" | "info" | "muted";

export function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-page px-4 py-10 text-body">{children}</main>
  );
}

export function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`mx-auto w-full max-w-md rounded-xl border border-line bg-surface p-8 shadow-sm ${className ?? ""}`}
    >
      {children}
    </div>
  );
}

const BANNER_TONE = {
  danger: { box: "border-danger/40 bg-danger/10 text-danger", Icon: TriangleAlert },
  success: { box: "border-accent/40 bg-accent/10 text-accent", Icon: CircleCheck },
  info: { box: "border-accent/40 bg-accent/10 text-accent", Icon: Info },
} as const;

export function Banner({
  children,
  tone = "danger",
}: {
  children: React.ReactNode;
  tone?: "danger" | "success" | "info";
}) {
  const { box, Icon } = BANNER_TONE[tone];
  return (
    <div
      role="alert"
      className={`mb-4 flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${box}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" strokeWidth={1.8} />
      <div>{children}</div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
  trend,
}: {
  label: string;
  value: string;
  hint?: string;
  trend?: { label: string; direction: "up" | "down" | "neutral" };
}) {
  const trendTone =
    trend?.direction === "up"
      ? "text-accent"
      : trend?.direction === "down"
        ? "text-danger"
        : "text-muted";
  const TrendIcon = trend?.direction === "down" ? TrendingDown : TrendingUp;
  return (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      <div className="text-sm font-medium text-muted">{label}</div>
      <div className="mt-1 font-mono text-2xl font-bold tracking-tight text-ink">
        {value}
      </div>
      {trend && (
        <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${trendTone}`}>
          {trend.direction !== "neutral" && (
            <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.8} />
          )}
          {trend.label}
        </div>
      )}
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

/**
 * Text field. Works uncontrolled (defaultValue, the submit handler reads the
 * form) or controlled (value + onChange), which the staged sign-up needs so it
 * can validate a step before advancing.
 *
 * An error clears the moment the field is edited, so a stale server error never
 * sits under a field the user has already corrected. A fresh error for the same
 * field re-arms it, tracked by comparing against the last error rendered.
 *
 * `prefix` renders a fixed, non-editable affix inside the control (the +961
 * dialling code); `icon` renders a leading glyph.
 */
/**
 * The label and the input surface `Field` renders, exported so the places that
 * cannot use `Field` still look like it.
 *
 * Not everything on a form is a labelled text input: a logo dropzone, a map
 * picker, a fieldset legend, a textarea, a pair of time inputs in a grid. Those
 * were each hand-rolling their own label and border, and had drifted to a
 * lighter weight and a tighter gap than `Field`, so a screen mixing the two
 * looked subtly unfinished without it being obvious why. One constant each,
 * used by both.
 */
export const FIELD_LABEL = "mb-1.5 block text-sm font-semibold text-ink";
export const FIELD_INPUT =
  "w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-body outline-none transition-colors duration-150 placeholder:text-muted focus:border-accent";

/** "Step N of T" plus a progress bar, so a staged form says where you are. */
export function StepIndicator({
  current,
  total,
}: {
  current: number;
  total: number;
}) {
  return (
    <div>
      <p className="mb-2 font-mono text-xs uppercase tracking-widest text-muted">
        Step {current} of {total}
      </p>
      <div className="flex gap-1.5" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-[2px] transition-colors duration-300 ${
              i < current ? "bg-accent" : "bg-line"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export function Field({
  label,
  name,
  type = "text",
  defaultValue,
  value,
  onChange,
  error,
  help,
  autoComplete,
  placeholder,
  inputMode,
  maxLength,
  disabled,
  readOnly,
  mono,
  icon: Icon,
  prefix,
  autoFocus,
  enterKeyHint,
}: {
  label: string;
  name: string;
  type?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  help?: string;
  autoComplete?: string;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "tel";
  maxLength?: number;
  disabled?: boolean;
  readOnly?: boolean;
  /** Use the data (mono) font, for codes, IDs, and other fixed-width input. */
  mono?: boolean;
  icon?: LucideIcon;
  prefix?: string;
  autoFocus?: boolean;
  /** What a touch keyboard's Return key says ("next" in a chain of fields). */
  enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send";
}) {
  const [edited, setEdited] = useState(false);
  const [lastError, setLastError] = useState(error);

  // A newly arrived error re-arms the field even if it was edited since.
  if (error !== lastError) {
    setLastError(error);
    setEdited(false);
  }

  const shown = edited ? undefined : error;
  const describedBy =
    [shown ? `${name}-error` : null, help ? `${name}-help` : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className="mb-4">
      <label htmlFor={name} className={FIELD_LABEL}>
        {label}
      </label>
      <div
        className={`flex items-center rounded-lg border bg-surface transition-colors duration-150 focus-within:border-accent ${
          shown ? "border-danger" : "border-line"
        } ${disabled || readOnly ? "bg-page" : ""}`}
      >
        {Icon && (
          <Icon
            className={`ms-3 size-4.5 shrink-0 ${shown ? "text-danger" : "text-muted"}`}
            aria-hidden
          />
        )}
        {prefix && (
          <span className="ms-3 shrink-0 border-e border-line pe-3 font-mono text-sm text-muted">
            {prefix}
          </span>
        )}
        <input
          id={name}
          name={name}
          type={type}
          defaultValue={onChange ? undefined : defaultValue}
          value={value}
          onChange={
            onChange
              ? (e) => {
                  setEdited(true);
                  onChange(e.target.value);
                }
              : () => setEdited(true)
          }
          autoComplete={autoComplete}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          enterKeyHint={enterKeyHint}
          aria-invalid={!!shown}
          aria-describedby={describedBy}
          className={`w-full bg-transparent px-3 py-2.5 text-body outline-none placeholder:text-muted read-only:text-muted disabled:cursor-not-allowed disabled:text-muted ${
            mono ? "font-mono tracking-widest" : ""
          }`}
        />
      </div>
      {help && !shown && (
        <p id={`${name}-help`} className="mt-1 text-xs text-muted">
          {help}
        </p>
      )}
      {shown && (
        <p
          id={`${name}-error`}
          className="mt-1.5 flex items-center gap-1.5 text-sm text-danger"
        >
          <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
          {shown}
        </p>
      )}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  type = "button",
  className,
  ...rest
}: {
  children: React.ReactNode;
  variant?: ButtonVariant;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${className ?? ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}

const BADGE_TONE: Record<Tone, { pill: string; dot: string }> = {
  success: { pill: "bg-accent/12 text-accent", dot: "bg-accent" },
  danger: { pill: "bg-danger/12 text-danger", dot: "bg-danger" },
  warning: { pill: "border border-line bg-transparent text-body", dot: "bg-muted" },
  info: { pill: "bg-accent/12 text-accent", dot: "bg-accent" },
  muted: { pill: "bg-line text-muted", dot: "bg-muted" },
};

/**
 * One badge language across consumer & business (design system §5). Pair with
 * bookingStatus() which owns the label + tone for each booking state.
 */
/**
 * Submit, with the pending state passed in (R22 T14).
 *
 * The counterpart to `useFormAction`: the Next kit's `SubmitButton` reads
 * `useFormStatus`, which reports only on a **form action**, so it could not
 * move here. This one is told, which is also the more honest arrangement since
 * the component that started the request is the one that knows.
 *
 * Same classes and same pending-label contract as the one it replaces, so a
 * screen reads identically on either host.
 */
export function ActionSubmit({
  children,
  pendingLabel,
  pending,
  variant = "primary",
  full = true,
}: {
  children: React.ReactNode;
  pendingLabel: string;
  pending: boolean;
  variant?: ButtonVariant;
  full?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={pending}
      className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${full ? "w-full" : ""}`}
    >
      {pending ? pendingLabel : children}
    </button>
  );
}

export function StatusBadge({
  tone,
  children,
}: {
  tone: Tone;
  children: React.ReactNode;
}) {
  const { pill, dot } = BADGE_TONE[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-0.5 text-xs font-semibold ${pill}`}
    >
      <span className={`h-1.5 w-1.5 rounded-[2px] ${dot}`} aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * Calm shimmer placeholder for loading states, never an in-page spinner
 * (design system §7). Animation collapses under prefers-reduced-motion.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-md bg-line/70 ${className ?? ""}`}
    />
  );
}
